import React, { useState } from 'react';
import { BellRing } from 'lucide-react';
import { messageErreur, reservationsApi, type Reservation } from '../../lib/reservationsApi';
import { maintenantParis } from './statuts';

/**
 * Bloc « Reconfirmation » de la fiche (comme ZenChef) : où en est la demande, « Demander la reconfirmation » (e-mail au client, ou relance)
 * et « Marquer reconfirmée » (le client a répondu par téléphone). Affiché pour une réservation confirmée ou à confirmer, à venir.
 */
interface Props { resa: Reservation; onMaj: (message?: string) => Promise<void> }
const lien = 'text-[12.5px] font-semibold underline min-h-[28px] max-lg:min-h-[44px]';
const quand = (l: string) => `le ${l.slice(8, 10)}/${l.slice(5, 7)} à ${l.slice(11, 16)}`;

export const BlocReconfirmation: React.FC<Props> = ({ resa, onMaj }) => {
    const [erreur, setErreur] = useState('');
    const [occupe, setOccupe] = useState(false);
    if (!['a_confirmer', 'confirmee'].includes(resa.statut) || resa.debut <= maintenantParis()) return null;
    const rc = resa.reconfirmation;
    const agir = async (fn: () => Promise<unknown>, message: string) => {
        setOccupe(true); setErreur('');
        try { await fn(); await onMaj(message); } catch (e) { setErreur(messageErreur(e)); } finally { setOccupe(false); }
    };
    return (
        <section aria-labelledby="fiche-reconf" className="rounded-xl border border-border p-3.5 space-y-2">
            <div className="flex items-center gap-2"><BellRing size={15} aria-hidden="true" /><h3 id="fiche-reconf" className="text-[12.5px] font-semibold text-content">Reconfirmation</h3></div>
            <p className="text-[13px] text-content">
                {!rc && 'Pas encore demandée.'}
                {rc?.etat === 'demandee' && `Demandée ${rc.demandee_le ? quand(rc.demandee_le) : ''} : en attente de la réponse du client.`}
                {rc?.etat === 'reconfirmee' && `Le client a reconfirmé ${rc.reponse_le ? quand(rc.reponse_le) : ''}.`}
            </p>
            {rc?.etat !== 'reconfirmee' && (
                <div className="flex flex-wrap gap-3">
                    {resa.email
                        ? <button type="button" className={lien} disabled={occupe} onClick={() => void agir(() => reservationsApi.demanderReconfirmation(resa.id), `Demande de reconfirmation envoyée à ${resa.email}.`)}>{rc ? 'Relancer le client' : 'Demander la reconfirmation'}</button>
                        : <span className="text-[12.5px] text-content-muted">Pas d’e-mail : appelez le client.</span>}
                    <button type="button" className={lien} disabled={occupe} onClick={() => void agir(() => reservationsApi.marquerReconfirmee(resa.id), 'Réservation marquée reconfirmée.')}>Marquer reconfirmée</button>
                </div>
            )}
            {erreur && <p role="alert" className="text-[12.5px] font-semibold text-state-danger">{erreur}</p>}
        </section>
    );
};

/** Petite mention à côté du statut, dans la liste et sur la fiche. */
export const MentionReconfirmation: React.FC<{ resa: Pick<Reservation, 'reconfirmation' | 'statut'> }> = ({ resa }) => {
    if (!resa.reconfirmation || !['a_confirmer', 'confirmee'].includes(resa.statut)) return null;
    const ok = resa.reconfirmation.etat === 'reconfirmee';
    return <span className={`inline-flex items-center h-6 px-2 rounded-full text-[11.5px] font-semibold whitespace-nowrap ${ok ? 'bg-state-ok-soft text-state-ok' : 'bg-surface-soft text-content-muted border border-border'}`}>{ok ? 'Reconfirmée' : 'Reconfirmation demandée'}</span>;
};
